import { TOOLS } from '../utils/constants.js';
import { PaintCommand } from '../core/EditorActions.js';

/**
 * Dispatches mouse events on the map canvas to the active tool.
 * Tools implement: onDown(tx, ty, e), onMove(tx, ty, e), onUp(tx, ty, e)
 *
 * Right-click drag defines a zone, then shows a tool picker popup
 * (scatter, fill with tile, clear area) to apply to the zone.
 */
export class ToolManager {
  constructor(state, compositor, mapCanvas, commandHistory) {
    this.state = state;
    this.compositor = compositor;
    this.mapCanvas = mapCanvas;
    this.commandHistory = commandHistory;
    this._tools = new Map();
    this._isDown = false;

    const canvas = compositor.canvas;
    canvas.addEventListener('mousedown', (e) => this._onDown(e));
    canvas.addEventListener('mousemove', (e) => this._onMove(e));
    canvas.addEventListener('mouseup', (e) => this._onUp(e));
    canvas.addEventListener('mouseleave', (e) => this._onLeave(e));
    canvas.addEventListener('wheel', (e) => this._onWheel(e), { passive: false });
    canvas.addEventListener('contextmenu', (e) => e.preventDefault());

    // Middle-click pan / Space+drag pan
    this._panning = false;
    this._panStart = { x: 0, y: 0 };
    this._spaceHeld = false;

    // Space bar = temporary hand tool (like Photoshop)
    document.addEventListener('keydown', (e) => {
      if (e.code === 'Space' && !e.repeat && !this._isInputFocused()) {
        this._spaceHeld = true;
        canvas.style.cursor = 'grab';
        e.preventDefault();
      }
      // Home key = reset view
      if (e.code === 'Home') {
        this.resetView();
        e.preventDefault();
      }
    });
    document.addEventListener('keyup', (e) => {
      if (e.code === 'Space') {
        this._spaceHeld = false;
        if (!this._panning) canvas.style.cursor = '';
      }
    });

    // Right-click zone selection
    this._rightDrag = false;
    this._rightStart = null;
    this._rightEnd = null;
  }

  _isInputFocused() {
    const tag = document.activeElement?.tagName;
    return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
  }

  registerTool(name, tool) {
    this._tools.set(name, tool);
  }

  getActiveTool() {
    return this._tools.get(this.state.get('activeTool'));
  }

  getTool(name) {
    return this._tools.get(name) || null;
  }

  _getCanvasPos(e) {
    const rect = this.compositor.canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  _onDown(e) {
    // Alt+click → eyedropper (pick tile from map)
    if (e.button === 0 && e.altKey) {
      this._eyedrop(e);
      return;
    }

    // Middle button → pan, or left-click while holding Space (hand tool)
    if (e.button === 1 || (e.button === 0 && this._spaceHeld)) {
      this._panning = true;
      this._panStart = { x: e.clientX, y: e.clientY };
      this.compositor.canvas.style.cursor = 'grabbing';
      e.preventDefault();
      return;
    }

    // Right button → zone selection drag
    if (e.button === 2) {
      const pos = this._getCanvasPos(e);
      const tile = this.compositor.screenToTile(pos.x, pos.y);
      this._rightDrag = true;
      this._rightStart = { x: tile.x, y: tile.y };
      this._rightEnd = { x: tile.x, y: tile.y };
      this.state.batch({
        selectionStart: { x: tile.x, y: tile.y },
        selectionEnd: { x: tile.x, y: tile.y },
      });
      this.compositor.requestRender();
      return;
    }

    if (e.button !== 0) return;

    const pos = this._getCanvasPos(e);
    const tile = this.compositor.screenToTile(pos.x, pos.y);
    const layer = this.state.getActiveLayer();
    if (!layer || layer.locked) return;

    this._isDown = true;
    const tool = this.getActiveTool();
    if (tool?.onDown) tool.onDown(tile.x, tile.y, e);
    this.compositor.requestRender();
  }

  _onMove(e) {
    // Pan with middle button or hand tool
    if (this._panning) {
      const dx = e.clientX - this._panStart.x;
      const dy = e.clientY - this._panStart.y;
      const newX = this.state.get('scrollX') - dx;
      const newY = this.state.get('scrollY') - dy;
      const clamped = this._clampScroll(newX, newY, this.state.get('zoom'));
      this.state.batch(clamped);
      this._panStart = { x: e.clientX, y: e.clientY };
      this.compositor.requestRender();
      return;
    }

    const pos = this._getCanvasPos(e);
    const tile = this.compositor.screenToTile(pos.x, pos.y);

    this.state.batch({
      cursorTileX: tile.x,
      cursorTileY: tile.y,
    });

    // Right-click zone drag
    if (this._rightDrag) {
      this._rightEnd = { x: tile.x, y: tile.y };
      this.state.batch({ selectionEnd: { x: tile.x, y: tile.y } });
      this.compositor.requestRender();
    }

    if (this._isDown) {
      const tool = this.getActiveTool();
      if (tool?.onMove) tool.onMove(tile.x, tile.y, e);
    }

    this.compositor.requestRender();
    this._updateStatus(tile.x, tile.y);
  }

  _onUp(e) {
    if (e.button === 1 || (e.button === 0 && this._panning)) {
      this._panning = false;
      this.compositor.canvas.style.cursor = this._spaceHeld ? 'grab' : '';
      return;
    }

    // Right-click zone release → show tool picker
    if (e.button === 2 && this._rightDrag) {
      this._rightDrag = false;
      const pos = this._getCanvasPos(e);
      const tile = this.compositor.screenToTile(pos.x, pos.y);
      this._rightEnd = { x: tile.x, y: tile.y };
      this._showZoneToolPicker(e.clientX, e.clientY);
      return;
    }

    if (!this._isDown) return;
    this._isDown = false;

    const pos = this._getCanvasPos(e);
    const tile = this.compositor.screenToTile(pos.x, pos.y);
    const tool = this.getActiveTool();
    if (tool?.onUp) tool.onUp(tile.x, tile.y, e);
    this.compositor.requestRender();
  }

  _onLeave(e) {
    this._panning = false;
    if (!this._spaceHeld) this.compositor.canvas.style.cursor = '';
    if (this._isDown) {
      this._isDown = false;
      const tool = this.getActiveTool();
      if (tool?.onUp) tool.onUp(-1, -1, e);
    }
    this.state.batch({ cursorTileX: -1, cursorTileY: -1 });
    this.compositor.requestRender();
  }

  _eyedrop(e) {
    const pos = this._getCanvasPos(e);
    const tile = this.compositor.screenToTile(pos.x, pos.y);
    const layers = this.state.get('layers');
    const activeIdx = this.state.get('activeLayerIndex');

    // Search from active layer down to find a non-empty tile
    for (let i = activeIdx; i >= 0; i--) {
      const layer = layers[i];
      if (!layer.visible || layer.type !== 'tilelayer' || !layer.data) continue;
      const mapW = this.state.get('mapWidth');
      if (tile.x < 0 || tile.y < 0 || tile.x >= mapW || tile.y >= this.state.get('mapHeight')) continue;
      const gid = layer.data[tile.y * mapW + tile.x];
      if (gid > 0) {
        // Strip flip flags to get the base tile ID
        const baseTileId = gid & 0x1FFFFFFF;
        this.state.set('selectedTileId', baseTileId);
        this.state.set('stamp', null);
        // Flash a brief cursor change as feedback
        this.compositor.canvas.style.cursor = 'crosshair';
        setTimeout(() => {
          if (!this._spaceHeld) this.compositor.canvas.style.cursor = '';
        }, 200);
        return;
      }
    }
  }

  _clampScroll(scrollX, scrollY, zoom) {
    const canvas = this.compositor.canvas;
    const viewW = canvas?.width || 800;
    const viewH = canvas?.height || 600;
    const mapW = this.state.get('mapWidth') * this.state.get('tileWidth') * zoom;
    const mapH = this.state.get('mapHeight') * this.state.get('tileHeight') * zoom;

    // Allow scrolling up to half a viewport past each edge so the map
    // can be centered even when it's smaller than the viewport
    const margin = 0.5;
    const minX = -(viewW * margin);
    const minY = -(viewH * margin);
    const maxX = mapW - viewW * (1 - margin);
    const maxY = mapH - viewH * (1 - margin);

    return {
      scrollX: Math.max(minX, Math.min(maxX, scrollX)),
      scrollY: Math.max(minY, Math.min(maxY, scrollY)),
    };
  }

  /** Reset the viewport to show the full map. */
  resetView() {
    this.state.batch({ scrollX: 0, scrollY: 0, zoom: 1 });
    this.compositor.requestRender();
  }

  _onWheel(e) {
    e.preventDefault();

    // Ctrl+wheel = zoom (toward cursor)
    if (e.ctrlKey || e.metaKey) {
      const zoom = this.state.get('zoom');
      // Proportional zoom — smaller steps at low zoom, larger at high zoom
      const factor = e.deltaY > 0 ? 0.9 : 1.1;
      const newZoom = Math.max(0.25, Math.min(8, zoom * factor));

      const pos = this._getCanvasPos(e);
      const scrollX = this.state.get('scrollX');
      const scrollY = this.state.get('scrollY');
      const worldX = (pos.x + scrollX) / zoom;
      const worldY = (pos.y + scrollY) / zoom;
      const newScrollX = worldX * newZoom - pos.x;
      const newScrollY = worldY * newZoom - pos.y;

      const clamped = this._clampScroll(newScrollX, newScrollY, newZoom);
      this.state.batch({ zoom: newZoom, ...clamped });
      this.compositor.requestRender();
      return;
    }

    // Plain wheel = scroll/pan (use actual pixel delta for smooth trackpad support)
    let dx = 0, dy = 0;

    if (e.shiftKey) {
      dx = e.deltaY;
    } else {
      dy = e.deltaY;
      dx = e.deltaX;
    }

    // Normalize — wheel events can give huge deltaY in 'line' or 'page' mode
    if (e.deltaMode === 1) { dx *= 20; dy *= 20; } // line mode
    if (e.deltaMode === 2) { dx *= 400; dy *= 400; } // page mode

    const newX = this.state.get('scrollX') + dx;
    const newY = this.state.get('scrollY') + dy;
    const clamped = this._clampScroll(newX, newY, this.state.get('zoom'));

    this.state.batch(clamped);
    this.compositor.requestRender();
  }

  _showZoneToolPicker(clientX, clientY) {
    const start = this._rightStart;
    const end = this._rightEnd;
    if (!start || !end) return;

    const x1 = Math.min(start.x, end.x);
    const y1 = Math.min(start.y, end.y);
    const x2 = Math.max(start.x, end.x);
    const y2 = Math.max(start.y, end.y);
    const w = x2 - x1 + 1;
    const h = y2 - y1 + 1;

    // Don't show picker for single-tile "click" (no drag)
    if (w <= 1 && h <= 1) {
      this.state.batch({ selectionStart: null, selectionEnd: null });
      this.compositor.requestRender();
      return;
    }

    const selectedTile = this.state.get('selectedTileId');
    const tileLabel = selectedTile ? ` (tile ${selectedTile})` : '';

    const menu = document.createElement('div');
    menu.className = 'menu-dropdown';
    menu.style.cssText = `position:fixed;left:${clientX}px;top:${clientY}px;z-index:10000;`;

    const items = [
      { label: `Scatter${tileLabel} in ${w}x${h} zone`, icon: '✦', action: 'scatter' },
      { label: `Fill zone${tileLabel}`, icon: '◕', action: 'fill' },
      { label: `Clear zone`, icon: '⌫', action: 'clear' },
    ];

    for (const item of items) {
      const el = document.createElement('div');
      el.className = 'menu-dropdown-item';
      const span = document.createElement('span');
      span.textContent = `${item.icon} ${item.label}`;
      el.appendChild(span);
      el.addEventListener('click', (ev) => {
        ev.stopPropagation();
        menu.remove();
        this._applyZoneTool(item.action, x1, y1, x2, y2);
      });
      menu.appendChild(el);
    }

    // Density submenu for scatter
    const densityEl = document.createElement('div');
    densityEl.className = 'menu-dropdown-item';
    densityEl.style.fontSize = '11px';
    densityEl.style.color = 'var(--text-dim)';
    const density = this.state.get('scatterDensity') ?? 0.15;
    densityEl.textContent = `Density: ${Math.round(density * 100)}%`;
    densityEl.addEventListener('click', (ev) => {
      ev.stopPropagation();
      const input = prompt('Scatter density (1-100%):', Math.round(density * 100));
      if (input) {
        const val = Math.max(1, Math.min(100, parseInt(input, 10))) / 100;
        this.state.set('scatterDensity', val);
        densityEl.textContent = `Density: ${Math.round(val * 100)}%`;
      }
    });
    menu.appendChild(document.createElement('div')).className = 'menu-separator';
    menu.appendChild(densityEl);

    document.body.appendChild(menu);

    const close = () => {
      menu.remove();
      this.state.batch({ selectionStart: null, selectionEnd: null });
      this.compositor.requestRender();
      document.removeEventListener('click', close);
    };
    setTimeout(() => document.addEventListener('click', close), 0);
  }

  _applyZoneTool(action, x1, y1, x2, y2) {
    const state = this.state;
    const layerIndex = state.get('activeLayerIndex');
    const layer = state.getActiveLayer();
    if (!layer || layer.locked) return;

    // Clear selection visual
    state.batch({ selectionStart: null, selectionEnd: null });

    if (action === 'scatter') {
      // Use ScatterTool directly on the zone
      const scatterTool = this._tools.get(TOOLS.SCATTER);
      if (scatterTool) {
        scatterTool._dragStart = { x: x1, y: y1 };
        scatterTool._dragEnd = { x: x2, y: y2 };
        const changes = scatterTool._scatter();
        if (changes.length > 0) {
          this.commandHistory.push(new PaintCommand(state, layerIndex, changes));
        }
        scatterTool._dragStart = null;
        scatterTool._dragEnd = null;
      }
    } else if (action === 'fill') {
      // Fill entire zone with selected tile
      const tileId = state.get('selectedTileId') || 0;
      const flipFlags = state.get('tileFlipFlags') || 0;
      const changes = [];
      const mapW = state.get('mapWidth');
      const mapH = state.get('mapHeight');

      for (let y = Math.max(0, y1); y <= Math.min(mapH - 1, y2); y++) {
        for (let x = Math.max(0, x1); x <= Math.min(mapW - 1, x2); x++) {
          const oldTile = state.getTile(layerIndex, x, y);
          const newTile = tileId ? (tileId | flipFlags) : 0;
          if (oldTile !== newTile) {
            state.setTile(layerIndex, x, y, newTile);
            changes.push({ x, y, oldTile, newTile });
          }
        }
      }

      if (changes.length > 0) {
        this.mapCanvas.invalidateLayer(layerIndex);
        import('../core/EditorActions.js').then(({ PaintCommand }) => {
          this.commandHistory.push(new PaintCommand(state, layerIndex, changes));
        });
      }
    } else if (action === 'clear') {
      // Clear zone (fill with 0)
      const changes = [];
      const mapW = state.get('mapWidth');
      const mapH = state.get('mapHeight');

      for (let y = Math.max(0, y1); y <= Math.min(mapH - 1, y2); y++) {
        for (let x = Math.max(0, x1); x <= Math.min(mapW - 1, x2); x++) {
          const oldTile = state.getTile(layerIndex, x, y);
          if (oldTile !== 0) {
            state.setTile(layerIndex, x, y, 0);
            changes.push({ x, y, oldTile, newTile: 0 });
          }
        }
      }

      if (changes.length > 0) {
        this.mapCanvas.invalidateLayer(layerIndex);
        import('../core/EditorActions.js').then(({ PaintCommand }) => {
          this.commandHistory.push(new PaintCommand(state, layerIndex, changes));
        });
      }
    }

    this.compositor.requestRender();
  }

  _updateStatus(tx, ty) {
    const posEl = document.getElementById('status-pos');
    const zoomEl = document.getElementById('status-zoom');
    if (posEl) posEl.textContent = `${tx}, ${ty}`;
    if (zoomEl) zoomEl.textContent = `${Math.round(this.state.get('zoom') * 100)}%`;
  }
}
